<script setup>

import Button from "primevue/button";
import FloatLabel from "primevue/floatlabel";
import {watch} from "vue";
import Avatar from "primevue/avatar";
import DatePicker from "primevue/datepicker";
import Select from "primevue/select";
import {ref, computed, onMounted, onUnmounted} from "vue";
import {isString, uniq} from "lodash";
import { trans } from 'laravel-vue-i18n';
// Tooltipy na přeskáčku: jeden dole, druhý nahoře, třetí dole atd.
const tooltipPositions = computed(() => {
    const positions = planBlocks.value.map(block => {
        const left = parseFloat(block.left);
        return {left};
    });

    const result = Array(positions.length).fill('down');

    for (let i = 0; i < positions.length; i++) {
        result[i] = i % 2 === 0 ? 'down' : 'up';
    }

    return result;
});

const props = defineProps({
    showWorkers: Boolean,
    workers: Array
})

const showUi = ref(false);
const selectedWorker = defineModel('selectedWorker');
const currentYear = ref(new Date().getFullYear());

const selectedPlan = defineModel('plan');
const plans = defineModel();
const deleted = defineModel('deleted');

const container = ref(null);

const months = ref([
    { identifier: trans('common.months_abbr.jan') },
    { identifier: trans('common.months_abbr.feb') },
    { identifier: trans('common.months_abbr.mar') },
    { identifier: trans('common.months_abbr.apr') },
    { identifier: trans('common.months_abbr.may') },
    { identifier: trans('common.months_abbr.jun') },
    { identifier: trans('common.months_abbr.jul') },
    { identifier: trans('common.months_abbr.aug') },
    { identifier: trans('common.months_abbr.sep') },
    { identifier: trans('common.months_abbr.oct') },
    { identifier: trans('common.months_abbr.nov') },
    { identifier: trans('common.months_abbr.dec') },
]);

const hoverX = ref(0);

const daysInYear = (year) => {
    return ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)) ? 366 : 365;
};

const daysCount = computed(() => daysInYear(currentYear.value));

const onMouseMove = (e) => {
    if (!container.value) return;
    const rect = container.value.getBoundingClientRect();
    const totalWidth = rect.width;
    const dayWidth = totalWidth / daysCount.value;
    const offsetX = e.clientX - rect.left;
    const dayIndex = Math.floor(offsetX / dayWidth);
    const date = new Date(currentYear.value, 0, 1);
    date.setDate(date.getDate() + dayIndex);
    hoverX.value = offsetX;
};

const dragging = ref(false);
const dragStartX = ref(0);
const dragCurrentX = ref(0);

const addNewPlan = (isFreeTime = false) => {
    let startDate = new Date();
    let endDate = new Date();
    endDate.setDate(endDate.getDate() + 7);

    let tries = 0;
    while (tries < 365) {
        const isOverlapping = plans.value.some(plan => {
            return !(endDate <= plan.valid?.from || startDate >= plan.valid?.to);
        });
        if (!isOverlapping) {
            plans.value.push({
                valid: {
                    from: new Date(startDate),
                    to: new Date(endDate),
                },
                type: isFreeTime ? 'vacation': 'regular',
                days: {
                    mon: { id: 'mon', label: trans('common.dow_long.mon'), selected: false, timeslots: [] },
                    tue: { id: 'tue', label: trans('common.dow_long.tue'), selected: false, timeslots: [] },
                    wed: { id: 'wed', label: trans('common.dow_long.wed'), selected: false, timeslots: [] },
                    thu: { id: 'thu', label: trans('common.dow_long.thu'), selected: false, timeslots: [] },
                    fri: { id: 'fri', label: trans('common.dow_long.fri'), selected: false, timeslots: [] },
                    sat: { id: 'sat', label: trans('common.dow_long.sat'), selected: false, timeslots: [] },
                    sun: { id: 'sun', label: trans('common.dow_long.sun'), selected: false, timeslots: [] },
                }
            });
            selectedPlan.value = plans.value.length - 1;
            return;
        }

        // Posuň plán o jeden den doprava a zkus znovu
        startDate.setDate(startDate.getDate() + 1);
        endDate.setDate(endDate.getDate() + 1);
        tries++;
    }

    console.warn('Nelze najít volné místo pro nový plán.');
};

const startDrag = (e) => {
    if (!container.value) return;
    const rect = container.value.getBoundingClientRect();
    dragging.value = true;
    dragStartX.value = e.clientX - rect.left;
    dragCurrentX.value = dragStartX.value;
};
const onDrag = (e) => {
    onMouseMove(e);
    if (dragging.value) {
        if (!container.value) return;
        const rect = container.value.getBoundingClientRect();
        dragCurrentX.value = e.clientX - rect.left;
    }
};

const endDrag = () => {
    if (dragging.value) {
        const rect = container.value.getBoundingClientRect();
        const totalWidth = rect.width;
        const dayWidth = totalWidth / daysCount.value;

        let startDay = Math.round(Math.min(dragStartX.value, dragCurrentX.value) / dayWidth);
        let endDay = Math.round(Math.max(dragStartX.value, dragCurrentX.value) / dayWidth);

        if (endDay - startDay < 7) {
            // Pokud nebylo tažení, tak neuložíme plán
            selectedPlan.value = 'default';
            dragging.value = false;
            return;
        }

        dragging.value = false;

        const startDate = new Date(currentYear.value, 0, 1);
        startDate.setDate(startDate.getDate() + startDay);

        const endDate = new Date(currentYear.value, 0, 1);
        endDate.setDate(endDate.getDate() + endDay);

        const isOverlapping = plans.value.some(plan => {
            return !(endDate <= plan.valid?.from || startDate >= plan.valid?.to);
        });


        if (!isOverlapping) {
            plans.value.push({
                valid: {
                    from: startDate,
                    to: endDate,
                },
                type: 'regular',
                days: {
                    mon: { id: 'mon', label: trans('common.dow_long.mon'), selected: false, timeslots: [] },
                    tue: { id: 'tue', label: trans('common.dow_long.tue'), selected: false, timeslots: [] },
                    wed: { id: 'wed', label: trans('common.dow_long.wed'), selected: false, timeslots: [] },
                    thu: { id: 'thu', label: trans('common.dow_long.thu'), selected: false, timeslots: [] },
                    fri: { id: 'fri', label: trans('common.dow_long.fri'), selected: false, timeslots: [] },
                    sat: { id: 'sat', label: trans('common.dow_long.sat'), selected: false, timeslots: [] },
                    sun: { id: 'sun', label: trans('common.dow_long.sun'), selected: false, timeslots: [] },
                }
            });
        }
    }
};

const dragStyle = computed(() => {
    const left = Math.min(dragStartX.value, dragCurrentX.value);
    const width = Math.abs(dragCurrentX.value - dragStartX.value);
    return `left: ${left}px; width: ${width}px;`;
});

const dragDates = computed(() => {
    if (!dragging.value) return null;
    const rect = container.value?.getBoundingClientRect();
    const totalWidth = rect?.width || 1;
    const dayWidth = totalWidth / daysCount.value;

    const startDay = Math.round(Math.min(dragStartX.value, dragCurrentX.value) / dayWidth);
    const endDay = Math.round(Math.max(dragStartX.value, dragCurrentX.value) / dayWidth);

    const startDate = new Date(currentYear.value, 0, 1);
    startDate.setDate(startDate.getDate() + startDay);

    const endDate = new Date(currentYear.value, 0, 1);
    endDate.setDate(endDate.getDate() + endDay);

    return {start: startDate, end: endDate};
});

const getColor = (index) => {
    const hue = 120; // zelená
    const saturation = 50;
    const lightness = 30 + (index * 10) % 50;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const containerWidth = ref(0);

const planBlocks = computed(() => {
    const rect = container.value?.getBoundingClientRect();
    const totalWidth = rect?.width || containerWidth.value || 80;
    const dayWidth = totalWidth / daysCount.value;
    return plans.value
        .map((plan, index) => {
            const from = isString(plan.valid?.from) ? new Date(Date.parse(plan.valid?.from)) : plan.valid?.from;
            const to = isString(plan.valid?.to) ? new Date(Date.parse(plan.valid?.to)) : plan.valid?.to;

            const start = Math.floor((from - new Date(currentYear.value, 0, 1)) / (1000 * 60 * 60 * 24));
            const end = Math.floor((to - new Date(currentYear.value, 0, 1)) / (1000 * 60 * 60 * 24));
            const isOverlapping = plans.value.some((other, i) => {
                if (i === index) return false;

                const oFrom = isString(other.valid?.from) ? new Date(Date.parse(other.valid?.from)) : other.valid?.from;
                const oTo = isString(other.valid?.to) ? new Date(Date.parse(other.valid?.to)) : other.valid?.to;

                const oStart = Math.floor((oFrom - new Date(currentYear.value, 0, 1)) / (1000 * 60 * 60 * 24));
                const oEnd = Math.floor((oTo - new Date(currentYear.value, 0, 1)) / (1000 * 60 * 60 * 24));
                return start < oEnd && end > oStart;
            });
            return isOverlapping ? null : {
                left: `${start * dayWidth}px`,
                width: `${(end - start) * dayWidth}px`,
            };
        })
        .filter(Boolean);
});

const updateLayout = () => {
    if (container.value) {
        // Vynutíme reaktivní změnu, aby Vue recomputed spustil
        containerWidth.value = container.value.getBoundingClientRect().width;
    }
};

onMounted(() => {
    window.addEventListener('resize', updateLayout);
    setTimeout(() => showUi.value = true, 200);
});

onUnmounted(() => {
    window.removeEventListener('resize', updateLayout);
});

const resizing = ref(false);
const resizeIndex = ref(null);
const resizeStartX = ref(0);
const originalEnd = ref(null);

const onResizeStart = (e, index) => {
    e.stopPropagation();
    resizing.value = true;
    resizeIndex.value = index;
    resizeStartX.value = e.clientX;
    originalEnd.value = plans.value[index].valid.to;
    window.addEventListener('mousemove', onResizing);
    window.addEventListener('mouseup', onResizeEnd);
};

const onResizing = (e) => {
    if (!resizing.value) return;

    const rect = container.value.getBoundingClientRect();
    const totalWidth = rect.width;
    const dayWidth = totalWidth / daysCount.value;

    const deltaX = e.clientX - resizeStartX.value;
    const dayDelta = Math.round(deltaX / dayWidth);

    let newEnd = new Date(originalEnd.value);
    newEnd.setDate(newEnd.getDate() + dayDelta);

    const minEnd = new Date(plans.value[resizeIndex.value].valid.from);
    minEnd.setDate(minEnd.getDate() + 7);
    if (newEnd <= minEnd) {
        newEnd = minEnd;
    }

    const isOverlapping = plans.value.some((plan, i) => {
        if (i === resizeIndex.value) return false;
        return !(newEnd <= plan.valid?.from || plans.value[resizeIndex.value].valid?.from >= plan.valid?.to);
    });

    if (!isOverlapping) {
        plans.value[resizeIndex.value].valid.to = newEnd;
    }
};

const onResizeEnd = () => {
    resizing.value = false;
    resizeIndex.value = null;
    window.removeEventListener('mousemove', onResizing);
    window.removeEventListener('mouseup', onResizeEnd);
};

const draggingPlan = ref(false);
const dragPlanIndex = ref(null);
const dragStartXPlan = ref(0);
const originalStart = ref(null);
const originalEndPlan = ref(null);

const onDragStart = (e, index) => {
    e.stopPropagation();
    draggingPlan.value = true;
    dragPlanIndex.value = index;
    dragStartXPlan.value = e.clientX;
    originalStart.value = new Date(plans.value[index].valid.from);
    originalEndPlan.value = new Date(plans.value[index].valid.to);
    window.addEventListener('mousemove', onPlanDrag);
    window.addEventListener('mouseup', onDragEnd);
};

const onPlanDrag = (e) => {
    if (!draggingPlan.value) return;

    const rect = container.value.getBoundingClientRect();
    const totalWidth = rect.width;
    const dayWidth = totalWidth / daysCount.value;

    const deltaX = e.clientX - dragStartXPlan.value;
    const dayDelta = Math.round(deltaX / dayWidth);

    let newStart = new Date(originalStart.value);
    let newEnd = new Date(originalEndPlan.value);
    newStart.setDate(newStart.getDate() + dayDelta);
    newEnd.setDate(newEnd.getDate() + dayDelta);

    const isOverlapping = plans.value.some((plan, i) => {
        if (i === dragPlanIndex.value) return false;
        return !(newEnd <= plan.valid?.from || newStart >= plan.valid?.to);
    });

    if (!isOverlapping) {
        plans.value[dragPlanIndex.value].valid.from = newStart;
        plans.value[dragPlanIndex.value].valid.to = newEnd;
    }
};

const onDragEnd = () => {
    draggingPlan.value = false;
    dragPlanIndex.value = null;
    window.removeEventListener('mousemove', onPlanDrag);
    window.removeEventListener('mouseup', onDragEnd);
};

const resizingLeft = ref(false);
const resizeLeftIndex = ref(null);
const resizeLeftStartX = ref(0);
const originalStartLeft = ref(null);

const onResizeStartLeft = (e, index) => {
    e.stopPropagation();
    resizingLeft.value = true;
    resizeLeftIndex.value = index;
    resizeLeftStartX.value = e.clientX;
    originalStartLeft.value = plans.value[index].valid.from;
    window.addEventListener('mousemove', onResizingLeft);
    window.addEventListener('mouseup', onResizeEndLeft);
};

const onResizingLeft = (e) => {
    if (!resizingLeft.value) return;

    const rect = container.value.getBoundingClientRect();
    const totalWidth = rect.width;
    const dayWidth = totalWidth / daysCount.value;

    const deltaX = e.clientX - resizeLeftStartX.value;
    const dayDelta = Math.round(deltaX / dayWidth);

    let newStart = new Date(originalStartLeft.value);
    newStart.setDate(newStart.getDate() + dayDelta);

    const minStart = new Date(plans.value[resizeLeftIndex.value].valid.to);
    minStart.setDate(minStart.getDate() - 7);
    if (newStart >= minStart) {
        newStart = minStart;
    }

    const isOverlapping = plans.value.some((plan, i) => {
        if (!plan.valid?.from) return false;
        if (i === resizeLeftIndex.value) return false;
        return !(plans.value[resizeLeftIndex.value].valid.to <= plan.valid?.from || newStart >= plan.valid?.to);
    });

    if (!isOverlapping) {
        plans.value[resizeLeftIndex.value].valid.from = newStart;
    }
};

const onResizeEndLeft = () => {
    resizingLeft.value = false;
    resizeLeftIndex.value = null;
    window.removeEventListener('mousemove', onResizingLeft);
    window.removeEventListener('mouseup', onResizeEndLeft);
};

const previousYear = () => {
    currentYear.value -= 1;
    // Necháme plány beze změny
};

const nextYear = () => {
    currentYear.value += 1;
    // Necháme plány beze změny
};
// Výběr plánu kliknutím
const selectPlan = (index) => {
    selectedPlan.value = index;
};
const selectDefaultPlan = () => {
    selectedPlan.value = 'default';
};

// Animace směru pro přechod mezi plány
const previousSelectedPlan = ref('default');

const slideDirection = computed(() => {
    if (selectedPlan.value === 'default' && previousSelectedPlan.value !== 'default') {
        return 'slide-left';
    } else if (selectedPlan.value !== 'default' && previousSelectedPlan.value === 'default') {
        return 'slide-right';
    } else {
        return 'fade';
    }
});

watch(selectedPlan, (newVal, oldVal) => {
    previousSelectedPlan.value = oldVal;
});

const workersCustomPlan = computed(() => {
    const plan = plans.value[selectedPlan.value === 'default' ? 0 : selectedPlan.value];

    if (!plan || plan.type === 'vacation')
        return [];
    const usedWorkers = uniq(
        Object.values(plan?.days)
            .flatMap(day => day.timeslots ?? [])
            .flatMap(slot => slot.id_reservation_workers ?? [])
    ) ?? [];


    const excludedWorkers = props.workers.filter(worker =>
        usedWorkers.includes(worker.id)
    );

    const includedWorkers = props.workers.filter(worker =>
        !usedWorkers.includes(worker.id)
    );

    const workersCustomPlan = [];

    if (excludedWorkers.length)
    {
        workersCustomPlan.push({
            label: trans('forms.workers_have_custom_plan'),
            hasCustom: true,
            items: excludedWorkers
        },)
    }

    if (includedWorkers.length)
    {
        workersCustomPlan.push({
            label: trans('forms.workers_use_default_plan'),
            hasCustom: true,
            items: includedWorkers
        },)
    }

    return workersCustomPlan;
})

</script>

<template>
    <div class="" id="plan-calendar">
        <div v-if="plans.length > 1" class="flex justify-between items-center w-full gap-4 border-b dark:border-neutral-700 p-2">
            <Button icon="pi pi-arrow-left" text severity="secondary" :label="String(currentYear-1)"
                    @click="previousYear"></Button>
            <p class="text-xl font-bold text-center grow">{{ currentYear }}</p>
            <Button icon="pi pi-arrow-right" text severity="secondary" icon-pos="right" :label="String(currentYear+1)"
                    @click="nextYear"></Button>
            <slot name="buttons"></slot>
        </div>
        <div
            v-if="plans.length > 1"
            class="grid grid-cols-12 select-none relative dark:border-neutral-700 border-b min-w-80"
            ref="container"
            @mousemove="onMouseMove"
        >
            <p v-for="month in months" class="text-center bg-neutral-100 dark:bg-neutral-900 dark:border-neutral-700 text-xs" :key="month.identifier">
                {{ month.identifier }}</p>
            <div class="col-span-12 relative">

                <div class="col-span-12 relative z-50 h-10"
                     ref="dragArea"
                     @mousedown="startDrag"
                     @mousemove="onDrag"
                     @mouseup="endDrag">

                    <!-- Aktuální tažení -->
                    <div v-if="dragging" :style="dragStyle"
                         class="absolute top-2 h-6 bg-blue-500 shadow z-50 rounded flex items-center px-2 text-xs text-white">
                        <template
                            v-if="Math.abs(dragCurrentX - dragStartX) / container.value?.getBoundingClientRect().width * daysCount > 100">
                            <div class="px-2">
                                {{
                                    dragDates
                                        ? dragDates.start.toLocaleDateString('cs-CZ', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            ...(dragDates.start.getFullYear() !== dragDates.end?.getFullYear() ? {year: 'numeric'} : {})
                                        })
                                        : ''
                                }}
                            </div>
                            <div class="flex-grow"></div>
                            <div class="px-2">
                                {{
                                    dragDates
                                        ? dragDates.end.toLocaleDateString('cs-CZ', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            ...(dragDates.start.getFullYear() !== dragDates.end?.getFullYear() ? {year: 'numeric'} : {})
                                        })
                                        : ''
                                }}
                            </div>
                        </template>
                        <template v-else>
                            <div
                                class="absolute left-1/2 -translate-x-1/2 bg-black text-nowrap text-white text-[10px] px-2 py-1 rounded shadow top-full mt-1">
                                {{
                                    dragDates
                                        ? dragDates.start.toLocaleDateString('cs-CZ', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            ...(dragDates.start.getFullYear() !== dragDates.end?.getFullYear() ? {year: 'numeric'} : {})
                                        })
                                        : ''
                                }}
                                -
                                {{
                                    dragDates
                                        ? dragDates.end.toLocaleDateString('cs-CZ', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            ...(dragDates.start.getFullYear() !== dragDates.end?.getFullYear() ? {year: 'numeric'} : {})
                                        })
                                        : ''
                                }}
                            </div>
                        </template>
                    </div>

                    <!-- Existující bloky -->
                    <div
                        v-if="showUi"
                        v-for="(block, i) in planBlocks"
                        :key="i"
                        @click.stop="selectPlan(i)"
                        :class="[
                        'absolute top-2 h-6 rounded shadow inset-4 z-50 text-nowrap flex items-center',
                        parseFloat(block.width) > 100 ? 'px-0' : '',
                        plans[i].type === 'vacation' ? 'bg-blue-200' : 'bg-blue-500',
                        selectedPlan === i ? (plans[i].type === 'vacation' ? 'ring-2 ring-blue-200 ring-offset-1' : 'ring-2 ring-blue-400 ring-offset-1') : ''
                      ]"
                        :style="`left: ${block.left}; width: ${block.width};`"
                        @mousedown.prevent.stop="onDragStart($event, i)"
                        style="cursor: grab;"
                    >
                        <div
                            class="!w-4 -ml-2 h-full flex justify-end max-md:hidden cursor-row-resize"
                            @mousedown.prevent.stop="onResizeStartLeft($event, i)"
                        >
                            <div class=" h-4 my-1 rounded-full w-[4px]" :class="plans[i].type === 'vacation' ? 'bg-blue-400' : 'bg-blue-800'"></div>
                        </div>
                        <template v-if="parseFloat(block.width) <= 100">
                            <div :class="[
                            'absolute left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded shadow',
                            tooltipPositions[i] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
                          ]">
                                {{
                                    new Date(plans[i].valid?.from).toLocaleDateString('cs-CZ', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        ...(new Date(plans[i].valid?.from)?.getFullYear() !== new Date(plans[i].valid.to)?.getFullYear() ? {year: 'numeric'} : {})
                                    })
                                }}
                                -
                                {{
                                    new Date(plans[i].valid?.to).toLocaleDateString('cs-CZ', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        ...(new Date(plans[i].valid?.from)?.getFullYear() !== new Date(plans[i].valid.to)?.getFullYear() ? {year: 'numeric'} : {})
                                    })
                                }}
                            </div>
                        </template>
                        <template v-if="parseFloat(block.width) > 100">
                            <div class="px-2 text-xs " :class="
                                plans[i].type === 'vacation' ? 'text-black' : 'text-white'">
                                {{
                                    new Date(plans[i].valid?.from).toLocaleDateString('cs-CZ', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        ...(new Date(plans[i].valid?.from)?.getFullYear() !== new Date(plans[i].valid.to)?.getFullYear() ? {year: 'numeric'} : {})
                                    })
                                }}
                            </div>
                        </template>
                        <div class="flex-grow"></div>
                        <template v-if="parseFloat(block.width) > 100">
                            <div class="px-2 text-xs  " :class="
                                plans[i].type === 'vacation' ? 'text-black' : 'text-white'">
                                {{
                                    new Date(plans[i].valid?.to).toLocaleDateString('cs-CZ', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        ...(new Date(plans[i].valid?.from)?.getFullYear() !== new Date(plans[i].valid.to)?.getFullYear() ? {year: 'numeric'} : {})
                                    })
                                }}
                            </div>
                        </template>
                        <div
                            class="w-4 -mr-2 h-full cursor-ew-resize max-md:hidden flex justify-start"
                            @mousedown.prevent.stop="onResizeStart($event, i)"
                        >
                            <div class="h-4 my-1 rounded-full w-[4px]" :class="plans[i].type === 'vacation' ? 'bg-blue-400' : 'bg-blue-900'"></div>
                        </div>
                    </div>
                </div>
                <div class="absolute top-0 bottom-0 w-full border-t dark:border-neutral-700 z-0">
                    <!-- Dny jako čárky -->
                    <div v-for="day in daysCount" :key="day"
                         class="absolute top-0 bottom-0 dark:border-neutral-700 !z-0 border-l"
                         :class="{
                     'border-dotted border-gray-300 opacity-0': day % 2 === 0,
                     'border-solid border-black': day % 30 === 0
                   }"
                         :style="`left: ${(day / daysCount) * 100}%`"></div>
                </div>
            </div>

        </div>
        <div class="border-b flex overflow-hidden md:h-14 dark:border-neutral-700">
            <transition :name="slideDirection" mode="out-in">
                <div :key="selectedPlan" :class="[
                    'flex w-full max-md:flex-wrap px-3 items-center p-2 gap-4',
                    selectedPlan === 'default' ? '' : 'bg-white dark:bg-neutral-800'
                  ]">
                    <Select v-if="showWorkers && workersCustomPlan.length" size="small" v-model="selectedWorker" class="w-48 !rounded-full" option-value="id" option-label="name" show-clear option-group-children="items" option-group-label="label" :options="workersCustomPlan" dropdown-icon="pi pi-users" v-tooltip="$t('forms.workers_select_tooltip')" :placeholder="$t('common.colleagues')">
                        <template #option="{ option }">
                            <div class="flex items-center gap-3">
                                <Avatar size="normal" class="!w-6 !h-6 !text-sm" :label="option.initials"></Avatar>
                                <p class="text-sm">{{option.name}}</p>
                            </div>
                        </template>
                    </Select>
                    <template v-if="selectedPlan === 'default'">
                        <p class="text-lg font-bold grow ml-1">
                            {{ $t('forms.plan_default') }}<span class="max-xl:hidden"> {{ $t('common.plan') }}</span>
                            <span v-if="selectedWorker" class="opacity-40"> {{ $t('forms.plan_for_worker', { name: workers.find(worker => worker.id === selectedWorker)?.genitive }) }}</span>
                        </p>
                        <div class="text-right max-md:hidden">
                            <p class="text-sm font-semibold text-nowrap">
                                {{ $t('forms.plan_add_prompt') }}
                            </p>
                            <p class="text-sm opacity-60 text-nowrap">
                                {{ plans.length ? $t('forms.plan_add_hint_drag') : $t('forms.plan_add_hint') }}
                            </p>
                        </div>
                        <Button icon="pi pi-plus" id="new-plan" size="small" @click="addNewPlan(false)" rounded></Button>
                        <Button icon="pi pi-plus" id="new-free" size="small" @click="addNewPlan(true)" severity="info" :label="$t('forms.plan_free_time')" rounded></Button>


                        <slot v-if="plans.length === 1" name="buttons"></slot>
                    </template>
                    <template v-else>
                        <Button @click="selectedPlan = 'default'"
                                size="small" rounded>
                            <template #default>
                                <i class="pi pi-chevron-left"></i>
                                <span class="md:hidden">
                                    {{ $t('forms.plan_back_mobile') }}
                                </span>
                                <span class="max-md:hidden">
                                    {{ $t('forms.plan_back_desktop') }}
                                </span>
                            </template>
                        </Button>
                        <p class="text-lg font-bold grow">
                            {{ plans[selectedPlan]?.type === 'vacation' ? $t('forms.plan_type_vacation') : $t('forms.plan_type_regular') }}
                        </p>
                        <div class="md:hidden">
                            <Button icon="pi pi-trash" severity="danger" size="small"
                                    @click="() => { selectedPlan = 'default'; plans.splice(selectedPlan, 1);}" rounded></Button>
                        </div>
                        <div v-if="plans[selectedPlan]" class="flex gap-4 max-md:w-full">
                            <FloatLabel variant="on" class="w-full">
                                <DatePicker id="plan_start" date-format="dd.mm.yy" v-model="plans[selectedPlan].valid.from" autocomplete="off" fluid />
                                <label for="plan_start">{{ plans[selectedPlan].type === 'vacation' ? $t('forms.plan_start_vacation') : $t('forms.plan_start_plan') }}</label>
                            </FloatLabel>
                            <FloatLabel variant="on" class="w-full">
                                <DatePicker id="plan_end" date-format="dd.mm.yy" v-model="plans[selectedPlan].valid.to" autocomplete="off" fluid />
                                <label for="plan_end">{{ plans[selectedPlan].type === 'vacation' ? $t('forms.plan_end_vacation') : $t('forms.plan_end_plan') }}</label>
                            </FloatLabel>
                        </div>
                        <div class="max-md:hidden">
                            <Button icon="pi pi-trash" severity="danger" size="small"
                                    @click="() => {
                                        selectedPlan = 'default'
                                        deleted.push({
                                            valid_from: plans[selectedPlan].valid.from,
                                            valid_to: plans[selectedPlan].valid.to,
                                            type: plans[selectedPlan].type,
                                            id: plans[selectedPlan].id,
                                        });
                                        plans.splice(selectedPlan, 1);
                                    }" rounded></Button>
                        </div>
                    </template>
                </div>
            </transition>
        </div>
    </div>
</template>

<style scoped lang="scss">
.w-2 {
    min-width: 8px;
}

.slide-right-enter-active, .slide-right-leave-active {
    transition: all 0.2s ease;
}

.slide-right-enter-from {
    transform: translateX(20%);
    opacity: 0;
}

.slide-right-leave-to {
    transform: translateX(-20%);
    opacity: 0;
}

.slide-left-enter-active, .slide-left-leave-active {
    transition: all 0.2s ease;
}

.slide-left-enter-from {
    transform: translateX(-20%);
    opacity: 0;
}

.slide-left-leave-to {
    transform: translateX(20%);
    opacity: 0;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
